/**
 * Services API — real backend calls.
 * See BACKEND_CONTRACT.md.
 */

import axiosInstance from './axiosInstance';

/**
 * List available services.
 *
 * BACKEND: GET /services
 * QUERY:   ?clinicCode=&category=&search=&page=&pageSize=
 *          When `clinicCode` is present, the backend returns only the
 *          services that clinic offers (clinic_services join). Used by the
 *          Services page once the user has entered a clinic.
 * SUCCESS: { data: { services: [...] }, meta: { total, page, pageSize } }
 *
 * @param {object} [params]
 */
export async function listServices(params = {}) {
  const res = await axiosInstance.get('/services', { params });
  return res.data;
}

/**
 * Get checkout / proforma + payment URL for a service at a given clinic.
 *
 * BACKEND: POST /services/:serviceId/checkout
 * BODY:    { clinicCode }   ← identifies which clinic receives payment
 * SUCCESS: { data: { proforma: {...}, paymentUrl: "..." } }
 * ERRORS:  404 CLINIC_NOT_FOUND if the clinic code is invalid
 *
 * @param {string} serviceId
 * @param {string} clinicCode - the code the clinic gives the in-person patient
 */
export async function checkoutService(
  serviceId, clinicCode, physicianPhone = null, consultationId = null,
) {
  const res = await axiosInstance.post(`/services/${serviceId}/checkout`, {
    clinicCode,
    ...(physicianPhone ? { physicianPhone } : {}),
    // Capture-first: bind the order to the consultation the client already
    // started and captured for, instead of opening a second one.
    ...(consultationId ? { consultationId } : {}),
  });
  return res.data.data;
}

// NOTE: there's no "confirmCheckout" frontend call in production.
// After the gateway redirects the user back, the backend has already
// learned the outcome via a server-to-server webhook from the gateway.
// The return URL carries an `orderId`; CheckoutReturnPage just fetches
// transaction details via `transactionsApi.getTransaction(orderId)`.
//
// The MOCK servicesApi adds a `confirmCheckout` helper because in mock
// mode there's no real gateway or webhook — the simulator page calls
// it directly to record the would-have-happened transaction. That
// function is mock-specific and intentionally not mirrored here.

export const servicesApi = { listServices, checkoutService };
export default servicesApi;
